import type { GpcClass, GpcRefLine, GpcSector, NotationKey } from '@/types';

// GPC display layer. The FE owns every GPC display string — the wire carries
// only kebab enum keys + an int scope (the cross-service contract with
// village's `gpc` module). Sector roman numerals, ref-line numbers, the ref
// string (`I.1.1`), and the per-(sector, scope) leaf description are all
// derived here from the structured payload. Mirrors the `renderX` pattern in
// `data/labels.ts`, kept in its own module because the GPC taxonomy is large.

const sectorRoman: Record<GpcSector, string> = {
  'stationary-energy': 'I',
  transport: 'II',
  waste: 'III',
  ippu: 'IV',
  afolu: 'V',
  'other-scope3': 'VI',
};

const sectorName: Record<GpcSector, string> = {
  'stationary-energy': 'Stationary Energy',
  transport: 'Transport',
  waste: 'Waste',
  ippu: 'IPPU',
  afolu: 'AFOLU',
  'other-scope3': 'Other Scope 3',
};

// The `.X` number of each reference line within its sector (the GPC ref
// digit). Follows village `RefLine` form order; numbers restart per sector.
const refLineNumber: Record<GpcRefLine, number> = {
  'residential-buildings': 1,
  // GPC keeps I.2 as one number; the CoS split shows Commercial and
  // Institutional as separate rows under the same ref number (mirrors
  // village `RefLine`).
  commercial: 2,
  institutional: 2,
  manufacturing: 3,
  'energy-industries': 4,
  'agriculture-forestry-fishing': 5,
  'non-specified': 6,
  'fugitive-coal': 7,
  'fugitive-oil-natural-gas': 8,
  'on-road': 1,
  railways: 2,
  'waterborne-navigation': 3,
  aviation: 4,
  'off-road': 5,
  'solid-waste-disposal': 1,
  'biological-treatment': 2,
  'incineration-and-open-burning': 3,
  wastewater: 4,
  'industrial-processes': 1,
  'product-use': 2,
  livestock: 1,
  land: 2,
  'aggregate-sources': 3,
  'other-scope3': 1,
};

const refLineName: Record<GpcRefLine, string> = {
  'residential-buildings': 'Residential buildings',
  commercial: 'Commercial buildings & facilities',
  institutional: 'Institutional buildings & facilities',
  manufacturing: 'Manufacturing industries & construction',
  'energy-industries': 'Energy industries',
  'agriculture-forestry-fishing': 'Agriculture, forestry & fishing activities',
  'non-specified': 'Non-specified sources',
  'fugitive-coal': 'Fugitive emissions from coal',
  'fugitive-oil-natural-gas': 'Fugitive emissions from oil & natural gas',
  'on-road': 'On-road transport',
  railways: 'Railways',
  'waterborne-navigation': 'Waterborne navigation',
  aviation: 'Aviation',
  'off-road': 'Off-road transport',
  'solid-waste-disposal': 'Solid waste disposal',
  'biological-treatment': 'Biological treatment of waste',
  'incineration-and-open-burning': 'Incineration & open burning',
  wastewater: 'Wastewater treatment & discharge',
  'industrial-processes': 'Industrial processes',
  'product-use': 'Product use',
  livestock: 'Livestock',
  land: 'Land',
  'aggregate-sources': 'Aggregate sources & non-CO₂ on land',
  'other-scope3': 'Other Scope 3',
};

const summaryClassName: Record<GpcClass, string> = {
  'energy-use': 'Energy use (all I except I.4.4)',
  'energy-generation-to-grid': 'Energy generation supplied to the grid (I.4.4)',
  transportation: 'Transportation (all II)',
  'waste-in-city': 'Generated in the city (III.X.1 + .2)',
  'waste-outside-city': 'Generated outside the city (III.X.3)',
  ippu: 'IPPU',
  afolu: 'AFOLU',
  'other-scope3': 'Other Scope 3',
};

const notationName: Record<NotationKey, string> = {
  no: 'NO',
  ne: 'NE',
  ie: 'IE',
  na: 'NA',
};

const renderGpcSector = (sector: GpcSector): string =>
  `${sectorRoman[sector]} · ${sectorName[sector]}`;

const renderGpcSummaryClass = (summaryClass: GpcClass): string =>
  summaryClassName[summaryClass];

const renderGpcRefLine = (refLine: GpcRefLine): string => refLineName[refLine];

const renderNotationKey = (notation: NotationKey): string =>
  notationName[notation];

// The GPC reference string. A sub-sector row is `<roman>.<number>` (e.g.
// `I.1`); a leaf adds the scope as the third digit (`I.1.1`). `scope` doubles
// as the ref third-digit today — everything is scope-aligned (village flags
// the future out-of-boundary-waste decoupling).
const gpcRefLineRef = (sector: GpcSector, refLine: GpcRefLine): string =>
  `${sectorRoman[sector]}.${refLineNumber[refLine]}`;

const gpcLeafRef = (
  sector: GpcSector,
  refLine: GpcRefLine,
  scope: number,
): string => `${gpcRefLineRef(sector, refLine)}.${scope}`;

// The "GHG emissions source" description for a leaf, keyed on (sector, scope).
// Sector I uses the design-note wording; the rest fall back to the GPC
// scope-framework descriptions, with a waste-specific override. Sector-by-
// sector refinement beyond I tracks the deferred per-line methodology work
// (village GPC notes) — only I/II/III are reachable today anyway.
const gpcLeafDescription = (sector: GpcSector, scope: number): string => {
  if (sector === 'stationary-energy') {
    switch (scope) {
      case 1:
        return 'Emissions from fuel combustion within the city boundary';
      case 2:
        return 'Emissions from grid-supplied energy consumed within the city boundary';
      case 3:
        return 'Transmission & distribution losses from grid-supplied energy';
    }
  }
  if (sector === 'waste') {
    switch (scope) {
      case 1:
        return 'Emissions from waste generated & treated within the city boundary';
      case 3:
        return 'Emissions from waste generated in the city but treated outside the boundary';
    }
  }
  switch (scope) {
    case 1:
      return 'Emissions from sources located within the city boundary';
    case 2:
      return 'Emissions from grid-supplied energy consumed within the city boundary';
    case 3:
      return 'Emissions occurring outside the city boundary from in-boundary activities';
    default:
      return '';
  }
};

export {
  renderGpcSector,
  renderGpcSummaryClass,
  renderGpcRefLine,
  renderNotationKey,
  gpcRefLineRef,
  gpcLeafRef,
  gpcLeafDescription,
};
